import { AutoAwesome, Check } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';

import { useAiRecommendations } from 'hooks/useAiRecommendations';
import useTranslate from 'hooks/useTranslate';
import appThemeV2 from 'theme/themeV2';
import type { AiRecommendationKind, CustomOptionInterface } from 'types';

type Option = CustomOptionInterface<string | number | boolean | null | undefined>;

interface AiAssistFieldProps {
    label: string;
    kind: AiRecommendationKind;
    countryCode: string;
    options: Option[];
    value: unknown;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    size?: 'small' | 'medium';
    onChange: (value: unknown) => void;
    onBlur: () => void;
}

interface AiAssistDialogProps {
    open: boolean;
    kind: AiRecommendationKind;
    countryCode: string;
    currentValue: unknown;
    onClose: () => void;
    onSelect: (id: number | string) => void;
}

const PROMPT_COPY = {
    occupation: {
        label: 'jobDescription',
        placeholder: 'jobDescriptionPlaceholder',
        hint: 'aiAssistantJobHint',
        action: 'getTitles',
    },
    fieldOfStudy: {
        label: 'description',
        placeholder: 'descriptionPlaceholder',
        hint: 'aiAssistantStudyHint',
        action: 'getStudyFields',
    },
} as const;

const SparkleBadge = ({ size = 40 }: { size?: number }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 2.5,
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
        >
            <AutoAwesome fontSize="small" />
        </Box>
    );
};

const AiAssistDialog = ({
    open,
    kind,
    countryCode,
    currentValue,
    onClose,
    onSelect,
}: AiAssistDialogProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [description, setDescription] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const { mutate, data, isPending } = useAiRecommendations();
    const prompt = PROMPT_COPY[kind];
    const suggestions = data ?? [];
    const showSkeletons = isPending && suggestions.length === 0;
    const showEmpty = hasSearched && !isPending && suggestions.length === 0;

    const handleFetch = () => {
        const trimmed = description.trim();
        if (!trimmed || !countryCode) return;
        setHasSearched(true);
        mutate({ kind, countryCode, description: trimmed });
    };

    return (
        <Dialog open={open} fullWidth maxWidth="sm" fullScreen={isMobile} scroll="paper">
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 1.5 }}>
                <Box sx={{ mt: 0.25 }}>
                    <SparkleBadge />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {translate('aiAssistant')}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontWeight: 400, lineHeight: 1.45 }}
                    >
                        {translate(prompt.hint)}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 2 }}>
                    <TextField
                        label={translate(prompt.label)}
                        placeholder={translate(prompt.placeholder)}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        multiline
                        minRows={4}
                        fullWidth
                        sx={{ mt: 1.5 }}
                    />

                    {showSkeletons ? (
                        <Stack spacing={1}>
                            {[0, 1, 2].map((row) => (
                                <Skeleton
                                    key={row}
                                    variant="rounded"
                                    height={52}
                                    sx={{ borderRadius: 2.5 }}
                                />
                            ))}
                        </Stack>
                    ) : null}

                    {showEmpty ? (
                        <Stack
                            spacing={0.75}
                            sx={{ alignItems: 'center', textAlign: 'center', py: 1.5 }}
                        >
                            <SparkleBadge size={44} />
                            <Typography variant="subtitle2">{translate('noResults')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {translate('aiTryMoreDetail')}
                            </Typography>
                        </Stack>
                    ) : null}

                    {suggestions.length > 0 ? (
                        <Stack spacing={1} sx={{ opacity: isPending ? 0.55 : 1 }}>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{ lineHeight: 1.4, letterSpacing: '0.08em' }}
                            >
                                {translate('aiSuggestions')}
                            </Typography>
                            <Stack spacing={1} sx={{ maxHeight: 280, overflow: 'auto' }}>
                                {suggestions.map((item, index) => {
                                    const selected =
                                        String(item.value) === String(currentValue ?? '');
                                    return (
                                        <ButtonBase
                                            key={`${String(item.value)}-${index}`}
                                            onClick={() => onSelect(item.value)}
                                            disabled={isPending}
                                            sx={{
                                                width: '100%',
                                                justifyContent: 'space-between',
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.5,
                                                borderRadius: 2.5,
                                                border: '1px solid',
                                                borderColor: selected
                                                    ? 'primary.main'
                                                    : 'rgba(148, 163, 184, 0.28)',
                                                bgcolor: selected
                                                    ? alpha(theme.palette.primary.main, 0.08)
                                                    : 'background.paper',
                                                textAlign: 'left',
                                                transition:
                                                    'border-color 0.2s ease, background-color 0.2s ease',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: alpha(
                                                        theme.palette.primary.main,
                                                        0.06
                                                    ),
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: selected ? 600 : 500,
                                                    color: 'text.primary',
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                            {selected ? (
                                                <Check
                                                    fontSize="small"
                                                    sx={{ color: 'primary.main', flexShrink: 0 }}
                                                />
                                            ) : null}
                                        </ButtonBase>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                }}
            >
                <Button variant="outlined" onClick={onClose}>
                    {translate('close')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleFetch}
                    disabled={isPending || description.trim() === ''}
                    startIcon={
                        isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />
                    }
                    sx={{ flex: { xs: '1 1 180px', sm: '0 0 auto' } }}
                >
                    {translate(prompt.action)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** Formik autosuggest with the recommendation sparkle inside the field. */
const AiAssistField = ({
    label,
    kind,
    countryCode,
    options,
    value,
    disabled,
    error,
    helperText,
    size = 'small',
    onChange,
    onBlur,
}: AiAssistFieldProps) => {
    const { translate } = useTranslate();
    const [open, setOpen] = useState(false);
    const selected = options.find((option) => option.value === value) || null;

    const handleSuggestion = (id: number | string) => {
        const match = options.find((option) => String(option.value) === String(id));
        onChange(match ? match.value : id);
        setOpen(false);
    };

    return (
        <>
            <Autocomplete
                fullWidth
                size={size}
                disabled={disabled}
                value={selected}
                options={options}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                onChange={(_, next) => {
                    onChange(next ? (typeof next === 'string' ? next : next.value) : '');
                }}
                onBlur={onBlur}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size={size}
                        label={label}
                        error={error}
                        helperText={helperText}
                        onBlur={onBlur}
                        slotProps={{
                            ...params.slotProps,
                            input: {
                                ...params.slotProps.input,
                                startAdornment: countryCode ? (
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        edge="start"
                                        aria-label={translate('aiAssistant')}
                                        disabled={disabled}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setOpen(true);
                                        }}
                                    >
                                        <AutoAwesome fontSize="small" />
                                    </IconButton>
                                ) : (
                                    params.slotProps.input.startAdornment
                                ),
                            },
                        }}
                    />
                )}
            />
            {open ? (
                <ThemeProvider theme={appThemeV2}>
                    <AiAssistDialog
                        open={open}
                        kind={kind}
                        countryCode={countryCode}
                        currentValue={value}
                        onClose={() => setOpen(false)}
                        onSelect={handleSuggestion}
                    />
                </ThemeProvider>
            ) : null}
        </>
    );
};

export default AiAssistField;
